import { getNormalizedApiUrl, joinUrlPaths } from '@/utils/helpers/url-helpers';
import { useNotificationStore } from '@/stores/notification';

// API Base URL
const apiUrl = getNormalizedApiUrl();

// Legacy Response Types (following your existing pattern)
interface LegacyApiResponse<T> {
  status: string;
  message?: string;
  data: T | null;
}

// ACTUAL Backend Response Format (from response_handler.py)
export interface SuccessResponse<T> {
  success: true;
  data: T;
  message?: string;
  meta?: {
    pagination?: {
      page: number;
      per_page: number;
      total: number;
      total_pages: number;
      has_previous: boolean;
      has_next: boolean;
    };
    [key: string]: any;
  };
}

export interface ErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details?: any;
  };
  meta?: {
    [key: string]: any;
  };
}

export type ModernApiResponse<T> = SuccessResponse<T> | ErrorResponse;

// Paginated Response is just a SuccessResponse with pagination meta
export type PaginatedResponse<T> = SuccessResponse<T[]>;

// Custom Error Class
export class ApiClientError extends Error {
  constructor(
    message: string,
    public code?: string,
    public details?: any,
    public statusCode?: number
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// API Client Class - Standalone implementation
export class ApiClient {
  private baseUrl: string;

  constructor() {
    this.baseUrl = apiUrl;
  }

  /**
   * Core request method
   */
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    params?: Record<string, any>
  ): Promise<T> {
    const url = params ? this.buildUrlWithParams(endpoint, params) : this.buildUrl(endpoint);
    
    // Get CSRF token from meta tag or cookie
    const csrfToken = this.getCSRFToken();
    
    const defaultHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    };

    if (csrfToken) {
      defaultHeaders['X-CSRFToken'] = csrfToken;
    }

    const config: RequestInit = {
      credentials: 'include',
      headers: {
        ...defaultHeaders,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      return await this.handleResponse<T>(response);
    } catch (error) {
      console.error('API Request failed:', error);
      throw new ApiClientError(
        'Network error occurred',
        'NETWORK_ERROR',
        error
      );
    }
  }

  /**
   * Handle HTTP response
   */
  private async handleResponse<T>(response: Response): Promise<T> {
    const contentType = response.headers.get('content-type');
    
    // Handle 204 No Content (typically from DELETE operations)
    if (response.status === 204) {
      return null as unknown as T;
    }

    // For JSON responses, parse the content first
    let responseData: any = null;
    if (contentType?.includes('application/json')) {
      try {
        responseData = await response.json();
      } catch (error) {
        console.error('Failed to parse JSON response:', error);
      }
    }

    if (!response.ok) {
      let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
      let errorDetails = null;

      // If we have structured error response, use it
      if (responseData?.error?.message) {
        errorMessage = responseData.error.message;
        errorDetails = responseData.error.details;
      }

      // Show user-friendly notifications for common errors (but not for 400 errors that might be handled by services)
      const notificationStore = useNotificationStore();

      if (response.status === 401) {
        notificationStore.showError({
          title: 'Authentication Required',
          text: 'Please log in to continue'
        });
        await this.handleUnauthorized();
      } else if (response.status === 403) {
        notificationStore.showError({
          title: 'Access Denied',
          text: 'You do not have permission to perform this action'
        });
      } else if (response.status >= 500) {
        notificationStore.showError({
          title: 'Server Error',
          text: 'A server error occurred. Please try again later.'
        });
      }

      // For 400 errors with structured response, return the response instead of throwing
      // This allows the service layer to handle the error appropriately
      if (response.status === 400 && responseData?.success === false) {
        return responseData as T;
      }

      throw new ApiClientError(
        errorMessage,
        this.getErrorCodeFromStatus(response.status),
        errorDetails,
        response.status
      );
    }

    // Return the parsed JSON data for successful responses
    if (responseData !== null) {
      return responseData;
    }

    // For non-JSON responses (like file downloads)
    return response as unknown as T;
  }

  /**
   * GET request for paginated modern endpoints
   */
  async getPaginated<T>(endpoint: string, params?: Record<string, any>): Promise<PaginatedResponse<T>> {
    return this.request<PaginatedResponse<T>>(endpoint, {
      method: 'GET'
    }, params);
  }

  /**
   * GET request for modern endpoints
   */
  async getModern<T>(endpoint: string, params?: Record<string, any>): Promise<ModernApiResponse<T>> {
    return this.request<ModernApiResponse<T>>(endpoint, {
      method: 'GET'
    }, params);
  }

  /**
   * POST request for modern endpoints
   */
  async postModern<T>(endpoint: string, data?: any): Promise<ModernApiResponse<T>> {
    return this.request<ModernApiResponse<T>>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * PUT request for modern endpoints
   */
  async putModern<T>(endpoint: string, data?: any): Promise<ModernApiResponse<T>> {
    return this.request<ModernApiResponse<T>>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  async patchModern<T>(endpoint: string, data?: any): Promise<ModernApiResponse<T>> {
    return this.request<ModernApiResponse<T>>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * DELETE request for modern endpoints
   */
  async deleteModern(endpoint: string): Promise<void> {
    await this.request<void>(endpoint, {
      method: 'DELETE'
    });
  }

  /**
   * Download file
   */
  async downloadFile(endpoint: string, params?: Record<string, any>, filename?: string): Promise<void> {
    const url = this.buildUrlWithParams(endpoint, params);
    
    try {
      const response = await fetch(url, {
        credentials: 'include',
        headers: {
          'Accept': 'application/octet-stream, text/csv, application/pdf'
        }
      });

      if (!response.ok) {
        throw new ApiClientError(`Download failed: ${response.statusText}`, 'DOWNLOAD_ERROR', null, response.status);
      }

      const blob = await response.blob();
      
      if (filename) {
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (error) {
      throw new ApiClientError(
        'Failed to download file',
        'DOWNLOAD_ERROR',
        error
      );
    }
  }

  /**
   * Build full URL
   */
  private buildUrl(endpoint: string): string {
    // Remove leading slash from endpoint if it exists to prevent double slashes
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
    return joinUrlPaths(this.baseUrl, cleanEndpoint);
  }

  /**
   * Build URL with query parameters
   */
  private buildUrlWithParams(endpoint: string, params?: Record<string, any>): string {
    const baseUrl = this.buildUrl(endpoint);
    
    if (!params || Object.keys(params).length === 0) {
      return baseUrl;
    }

    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(item => searchParams.append(key, String(item)));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    const queryString = searchParams.toString();
    return queryString ? `${baseUrl}?${queryString}` : baseUrl;
  }

  /**
   * Handle a 401 response: clear client-side auth state and redirect to login,
   * preserving the current location as a `redirect` query param.
   *
   * Uses dynamic imports for the auth store and router to avoid circular
   * imports at module load time.
   */
  private async handleUnauthorized(): Promise<void> {
    try {
      const currentPath = window.location.pathname + window.location.search;

      // Clear client-side auth state. A 401 means the session is already dead,
      // so we clear locally rather than calling the API logout endpoint.
      const { useAuthStore } = await import('@/stores/auth');
      const authStore = useAuthStore();
      authStore.user = null;
      localStorage.removeItem('user');

      const { default: router } = await import('@/router');

      // Avoid redirect loops if we're already on the login page.
      if (router.currentRoute.value.path === '/auth/login') {
        return;
      }

      router.push({
        path: '/auth/login',
        query: { redirect: currentPath }
      });
    } catch (error) {
      // Never let auth-cleanup failures mask the original API error.
      console.error('Failed to handle unauthorized response:', error);
    }
  }

  /**
   * Get CSRF token from meta tag or cookie
   */
  private getCSRFToken(): string | null {
    // Try meta tag first
    const metaTag = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement;
    if (metaTag) {
      return metaTag.content;
    }

    // Try cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return decodeURIComponent(value);
      }
    }

    return null;
  }

  /**
   * Get error code from HTTP status
   */
  private getErrorCodeFromStatus(status: number): string {
    switch (status) {
      case 400: return 'VALIDATION_ERROR';
      case 401: return 'AUTHENTICATION_ERROR';
      case 403: return 'AUTHORIZATION_ERROR';
      case 404: return 'NOT_FOUND';
      case 429: return 'RATE_LIMIT_ERROR';
      case 500: return 'SERVER_ERROR';
      default: return 'HTTP_ERROR';
    }
  }

  /**
   * Check if response indicates success
   */
  isSuccessResponse<T>(response: ModernApiResponse<T>): response is SuccessResponse<T> {
    return response.success === true;
  }

  /**
   * Check if response indicates error
   */
  isErrorResponse<T>(response: ModernApiResponse<T>): response is ErrorResponse {
    return response.success === false;
  }

  /**
   * Extract error message from error response
   */
  getErrorMessage(response: ErrorResponse): string {
    return response.error.message;
  }

  /**
   * Extract error code from error response
   */
  getErrorCode(response: ErrorResponse): string {
    return response.error.code;
  }

  /**
   * Extract error details from error response
   */
  getErrorDetails(response: ErrorResponse): any {
    return response.error.details;
  }

  /**
   * Get success message from response
   */
  getSuccessMessage(response: SuccessResponse<any>): string | null {
    return response.message || null;
  }

  /**
   * Check if response has a success message
   */
  hasSuccessMessage(response: SuccessResponse<any>): boolean {
    return !!response.message;
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
export default apiClient; 